import { Component, EventEmitter, inject, Input, model, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { DatePickerDialogComponent } from '../date-picker-dialog/date-picker-dialog.component';
import { HighlightedDates } from '../../data/models/highilighted-dates.model';
import { isSameDate } from '../../util/shared.util';

export interface DialogData {
  selectedDate: Date;
}

@Component({
  selector: 'app-date-picker',
  imports: [CommonModule, MatDialogModule],
  templateUrl: './date-picker.component.html',
  styleUrl: './date-picker.component.scss',
})
export class DatePickerComponent implements OnChanges {
  @Input() color!: string;
  @Input() highlightedDates!: HighlightedDates[];
  @Input() date = new Date();
  @Input() disabled = false;
  @Output() dateSelected = new EventEmitter<Date>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['highlightedDates']) {
      this.date = this.highlightedDates?.length ? this.highlightedDates[0]?.date : new Date();
    }

    if (changes['disabled']) {
      this.disabled = changes['disabled'].currentValue;
    }
  }

  languageCode: 'da-DK' | 'en-GB' | 'sv-SE' = 'en-GB';
  dialog = inject(MatDialog);
  selectedDate = model<Date | null>(null);

  get previousIsDisabled(): boolean {
    if (!this.highlightedDates?.length) {
      return true;
    }

    return this.date.getTime() === this.highlightedDates[this.highlightedDates.length - 1]?.date.getTime();
  }

  get nextIsDisabled(): boolean {
    if (!this.highlightedDates?.length) {
      return true;
    }

    return this.date.getTime() === this.highlightedDates[0]?.date.getTime();
  }

  get monthName(): string {
    const currentYear = new Date().getFullYear();
    const selectedYear = this.date.getFullYear();
    const month = this.date.toLocaleString(this.languageCode, { month: 'short' }).slice(0, 3);
    const year = this.date.toLocaleString('default', { year: '2-digit' });

    if (selectedYear < currentYear) {
      return `${month} '${year}`;
    }

    return month;
  }

  get day(): number {
    return this.date.getDate();
  }

  previousDate(): void {
    const selectedIndex = this.highlightedDates.findIndex((highlightedDate) => isSameDate(highlightedDate.date, this.date));

    if (selectedIndex === -1 || selectedIndex + 1 >= this.highlightedDates.length) {
      return;
    }

    const newDate = this.highlightedDates[selectedIndex + 1]?.date;
    this.dateSelected.emit(newDate);
  }

  nextDate(): void {
    const selectedIndex = this.highlightedDates.findIndex((highlightedDate) => isSameDate(highlightedDate.date, this.date));

    if (selectedIndex === -1 || selectedIndex - 1 < 0) {
      return;
    }

    const newDate = this.highlightedDates[selectedIndex - 1]?.date;
    this.dateSelected.emit(newDate);
  }

  openDialog(): void {
    if (this.disabled) {
      return;
    }

    const dialogRef = this.dialog.open(DatePickerDialogComponent, {
      minWidth: '500px',
      data: { selectedDate: this.selectedDate() },
    });

    dialogRef.componentInstance.highlightDates = this.highlightedDates;
    dialogRef.componentInstance.languageCode = this.languageCode;
    dialogRef.componentInstance.selected = this.date;
    dialogRef.afterClosed().subscribe((selectedDate: Date | null) => {
      if (!selectedDate) {
        return;
      }

      this.date = selectedDate;
      this.dateSelected.emit(selectedDate);
    });
  }
}
